// src/features/learner/components/ContentRenderers/ContentRenderer.js
import React from 'react';
import { StoryRenderer } from './StoryRenderer';
import { MCQRenderer } from './MCQRenderer';
import { TypeInRenderer } from './TypeInRenderer';
import InteractiveImageViewer from './InteractiveImageViewer';
import { MultipleResponseRenderer } from './MultipleResponseRenderer';
import FlipCardRenderer from './FlipCardRenderer';
import { LRCRenderer } from './LRCRenderer';
import { MatchingRenderer } from './MatchingRenderer';
import { OrderRenderer } from './OrderRenderer';
import { ImageMCQRenderer } from './ImageMCQRenderer';

export const ContentRenderer = ({
    content,
    userAnswers,
    showFeedback,
    completedUnits,
    currentInternalIndex,
    internalContent,
    onAnswerSelect,
    onCheckAnswer,
    onMarkCompleted,
    onResetUnit,
    getRandomizedOptions
}) => {
    if (!content) {
        return (
            <div className="unit-content">
                <p>No content available</p>
            </div>
        );
    }

    const commonProps = {
        unit: content,
        userAnswers,
        showFeedback,
        completedUnits,
        onAnswerSelect,
        onCheckAnswer,
        onMarkCompleted,
        getRandomizedOptions
    };

    switch (content.type) {
        case 'story':
        case 'discussion':
        case 'Assignment':
            return (
                <StoryRenderer
                    {...commonProps}
                    currentInternalIndex={currentInternalIndex}
                    internalContent={internalContent}
                />
            );

        case 'MCQ':
            return <MCQRenderer {...commonProps} />;

        case 'image-mcq':
            return <ImageMCQRenderer {...commonProps} />;

        case 'Multiple-response':
            return <MultipleResponseRenderer {...commonProps} />;

        case 'Type-in':
            return <TypeInRenderer {...commonProps} />;

        case 'interactive-image':
            return <InteractiveImageViewer unit={content} />;

        case 'flipcard':
            return (
                <div className="unit-content">
                    <FlipCardRenderer
                        key={content.id}
                        unit={content}
                        completedUnits={completedUnits}
                        onComplete={(unitId, status) => {
                            if (onMarkCompleted) onMarkCompleted(unitId, null, status);
                        }}
                        onResetUnit={onResetUnit}
                    />
                </div>
            );

        case 'lrc':
        case 'LRC':
        case 'listen-record-compare':
            return (
                <div className="unit-content">
                    <LRCRenderer
                        key={content.id}
                        unit={content}
                        completedUnits={completedUnits}
                        onComplete={(unitId, status) => {
                            if (onMarkCompleted) onMarkCompleted(unitId, null, status);
                        }}
                    />
                </div>
            );

        case 'Matching':
            return (
                <MatchingRenderer
                    key={content.id}
                    unit={content}
                    userAnswers={userAnswers}
                    showFeedback={showFeedback}
                    completedUnits={completedUnits}
                    onAnswerSelect={onAnswerSelect}
                    onCheckAnswer={onCheckAnswer}
                    onMarkCompleted={onMarkCompleted}
                    onResetUnit={onResetUnit}
                />
            );

        case 'Order':
            return (
                <OrderRenderer
                    key={content.id}
                    unit={content}
                    userAnswers={userAnswers}
                    showFeedback={showFeedback}
                    completedUnits={completedUnits}
                    onAnswerSelect={onAnswerSelect}
                    onCheckAnswer={onCheckAnswer}
                    onMarkCompleted={onMarkCompleted}
                    onResetUnit={onResetUnit}
                />
            );

        default:
            return (
                <div className="unit-content">
                    <h2 className="unit-title">{content.title}</h2>
                    <p>Content type: {content.type}</p>
                    <div dangerouslySetInnerHTML={{
                        __html: typeof content.content === 'string'
                            ? content.content
                            : JSON.stringify(content.content)
                    }} />
                </div>
            );
    }
};